import { useState, useRef, useEffect, useLayoutEffect, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { ChevronDown, ChevronRight, Plus, Trash2, Minus, GripVertical, FolderInput } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import CardRow from './CardRow';
import ColumnHeader from './ColumnHeader';
import GroupFooter from './GroupFooter';
import ResizeHandle from './ResizeHandle';
import BoardPickerPopup from './BoardPickerPopup';
import useBoardStore from '../../store/boardStore';
import { dialog } from '../../store/dialogStore';
import useToastStore from '../../store/toastStore';
import useBoardCan from '../../hooks/useBoardCan';


function NewItemRow({ groupId, columnsCount, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `group-end:${groupId}`,
    data: { type: 'group', groupId },
  });
  return (
    <tr ref={setNodeRef}>
      <td colSpan={columnsCount + 2} className={`border-b border-[color:var(--group-border)] p-0 transition-colors ${isOver ? 'bg-accent/10' : ''}`}>
        {children}
      </td>
    </tr>
  );
}

// Paleta padrao Monday — usada pra trocar a cor do grupo (titulo + barra lateral)
const GROUP_PALETTE = [
  ['#037f4c', '#00c875', '#9cd326', '#cab641', '#ffcb00'],
  ['#784bd1', '#a25ddc', '#0086c0', '#579bfc', '#66ccff'],
  ['#bb3354', '#e2445c', '#ff158a', '#ff5ac4', '#ff642e'],
  ['#fdab3d', '#7f5347', '#c4c4c4', '#757575'],
];

function GroupColorSwatch({ currentColor, onPick, anchorRef }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const swatchRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchorRef?.current) return;
    const recompute = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const swatchHeight = swatchRef.current?.offsetHeight || 120;
      const vh = window.innerHeight;
      const spaceBelow = vh - rect.bottom;
      const flipUp = spaceBelow < swatchHeight + 8 && rect.top > spaceBelow;
      const top = flipUp
        ? rect.top + window.scrollY - swatchHeight - 4
        : rect.bottom + window.scrollY + 4;
      setPos({ top, left: rect.left + window.scrollX });
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [anchorRef]);

  return createPortal(
    <div
      ref={swatchRef}
      className="bg-card border border-border rounded-lg shadow-xl p-2 flex flex-col gap-1.5"
      style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 9999 }}
    >
      {GROUP_PALETTE.map((row, rIdx) => (
        <div key={rIdx} className="flex gap-1.5">
          {row.map((c) => (
            <button
              key={c}
              type="button"
              /* preventDefault evita o blur do input antes do onClick disparar */
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(c)}
              className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                currentColor?.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-white/70' : ''
              }`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>
      ))}
    </div>,
    document.body
  );
}

function GroupSection({ group, columns, cards = [], onAddColumn, selectedIds, onToggleSelect, onToggleSelectGroup, forceCollapsed = false, draggable = false, compact = false, onGripPress }) {
  const { currentBoard, editMode } = useBoardStore(
    useShallow((s) => ({
      currentBoard: s.currentBoard,
      editMode: s.editMode,
    }))
  );
  const updateGroup = useBoardStore((s) => s.updateGroup);
  const archiveGroup = useBoardStore((s) => s.archiveGroup);
  const createCard = useBoardStore((s) => s.createCard);
  const updateColumn = useBoardStore((s) => s.updateColumn);
  const updateBoard = useBoardStore((s) => s.updateBoard);
  const moveGroupToBoard = useBoardStore((s) => s.moveGroupToBoard);
  const showToast = useToastStore((s) => s.show);
  const can = useBoardCan();
  // edit_content: criar/mover/arquivar CARD (repassado como canCreate pro CardRow).
  // edit_structure: grupo, coluna, opcoes de status — grip, renomear, arquivar
  // e mover grupo, adicionar coluna. No fallback antigo os dois sao identicos
  // (mesma formula isOrgAdmin||role admin/editor), entao nada muda pra quem
  // ainda esta no backend M1a.
  const canEditContent = can.edit_content;
  const canEditStructure = can.edit_structure;
  const [titleColWidth, setTitleColWidth] = useState(() => currentBoard?.title_col_width ?? 280);
  useEffect(() => { setTitleColWidth(currentBoard?.title_col_width ?? 280); }, [currentBoard?.title_col_width]);
  const [liveWidths, setLiveWidths] = useState({});
  const setLiveWidth = (colId, w) => setLiveWidths((prev) => ({ ...prev, [colId]: w }));
  const clearLiveWidth = (colId) => setLiveWidths((prev) => { const n = { ...prev }; delete n[colId]; return n; });
  const cardIds = useMemo(() => cards.map((c) => `card:${c.id}`), [cards]);
  const [collapsed, setCollapsed] = useState(group.collapsed);
  const effectiveCollapsed = forceCollapsed || collapsed;

  const dragHandle = useDraggable({
    id: `group:${group.id}`,
    data: { type: 'group', groupId: group.id },
    disabled: !draggable,
  });
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(group.name);
  const [showNewCard, setShowNewCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [showBoardPicker, setShowBoardPicker] = useState(false);
  const moveBtnRef = useRef(null);
  const nameRef = useRef(null);
  const newCardRef = useRef(null);

  useEffect(() => {
    setName(group.name);
  }, [group.name]);

  useEffect(() => {
    if (editingName && nameRef.current) {
      nameRef.current.focus();
      nameRef.current.select();
    }
  }, [editingName]);

  useEffect(() => {
    if (showNewCard && newCardRef.current) {
      newCardRef.current.focus({ preventScroll: true });
    }
  }, [showNewCard]);

  const saveName = () => {
    setEditingName(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== group.name) {
      updateGroup(group.id, { name: trimmed });
    } else {
      setName(group.name);
    }
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    updateGroup(group.id, { collapsed: next });
  };

  const handleCreateCard = async () => {
    const t = newCardTitle.trim();
    if (!t) return;
    await createCard(group.id, t);
    setNewCardTitle('');
    setShowNewCard(false);
  };

  const handleCreateDivider = async () => {
    await createCard(group.id, '', { isDivider: true });
  };

  const totalTableWidth =
    titleColWidth + columns.reduce((sum, col) => sum + (liveWidths[col.id] ?? col.width ?? 150), 0) + 40;

  return (
    <div
      ref={draggable ? dragHandle.setNodeRef : undefined}
      className={`transition-all ${
        compact || effectiveCollapsed
          ? 'mb-0 border-b border-border/40'
          : 'mb-[72px]'
      } ${dragHandle.isDragging ? 'opacity-30' : ''}`}
    >
      <table className="border-separate border-spacing-0" style={{ tableLayout: 'fixed', width: totalTableWidth }}>
        <colgroup>
          <col style={{ width: titleColWidth }} />
          {columns.map((col) => (
            <col key={col.id} style={{ width: liveWidths[col.id] ?? col.width ?? 150 }} />
          ))}
          <col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr>
            <th
              colSpan={columns.length + 2}
              className="text-left p-0"
            >
              <div className="group/header flex items-center gap-2 px-3 py-2.5 sticky left-0 z-10 w-max">
                {draggable && canEditStructure && (
                  <button
                    ref={dragHandle.setActivatorNodeRef}
                    {...dragHandle.listeners}
                    {...dragHandle.attributes}
                    onPointerDown={(e) => {
                      onGripPress?.(group.id);
                      dragHandle.listeners?.onPointerDown?.(e);
                    }}
                    className="text-text-secondary/60 hover:text-text-primary cursor-grab active:cursor-grabbing opacity-0 group-hover/header:opacity-100 transition-opacity"
                    title="Arrastar grupo"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={toggleCollapse}
                  className="text-text-secondary hover:text-text-primary transition-colors"
                >
                  {effectiveCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {editingName ? (
                  <div className="relative flex items-center gap-2">
                    <input
                      ref={nameRef}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onBlur={saveName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveName();
                        if (e.key === 'Escape') {
                          setName(group.name);
                          setEditingName(false);
                        }
                      }}
                      className="group-title-text font-bold bg-transparent border border-accent/50 rounded outline-none"
                      style={{ color: group.color }}
                    />
                    <GroupColorSwatch
                      currentColor={group.color}
                      anchorRef={nameRef}
                      onPick={(c) => updateGroup(group.id, { color: c })}
                    />
                  </div>
                ) : (
                  <span
                    onClick={() => canEditStructure && setEditingName(true)}
                    className={`group-title-text font-bold ${canEditStructure ? 'cursor-pointer hover:opacity-80' : ''}`}
                    style={{ color: group.color }}
                  >
                    {group.name}
                  </span>
                )}
                <span className="text-[13px] text-text-secondary ml-1">
                  {cards.length} {cards.length === 1 ? 'item' : 'itens'}
                </span>
                {canEditStructure && (
                  <button
                    onClick={async () => {
                      if (await dialog.confirm('Mover para a lixeira', `Mover o grupo "${group.name}" para a lixeira?`, { confirmLabel: 'Mover', danger: true })) archiveGroup(group.id);
                    }}
                    className="p-1 rounded hover:bg-white/10 text-text-secondary hover:text-danger transition-colors ml-1"
                    title="Mover para lixeira"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {canEditStructure && (
                  <div className="relative ml-1">
                    <button
                      ref={moveBtnRef}
                      onClick={() => setShowBoardPicker((v) => !v)}
                      className="p-1 rounded hover:bg-white/10 text-text-secondary hover:text-text-primary transition-colors"
                      title="Mover grupo para outro board"
                    >
                      <FolderInput className="w-3.5 h-3.5" />
                    </button>
                    {showBoardPicker && (
                      <BoardPickerPopup
                        currentBoardId={currentBoard?.id}
                        anchorRef={moveBtnRef}
                        onClose={() => setShowBoardPicker(false)}
                        onSelect={async (targetBoard) => {
                          setShowBoardPicker(false);
                          const confirmed = await dialog.confirm(
                            'Mover grupo para outro board',
                            `Mover grupo "${group.name}" para o board "${targetBoard.name}"? Todos os itens irão junto.`,
                            { confirmLabel: 'Mover', danger: false }
                          );
                          if (!confirmed) return;
                          try {
                            const result = await moveGroupToBoard(group.id, targetBoard.id);
                            showToast({
                              message: `Grupo "${group.name}" movido para "${targetBoard.name}" com ${result.cards_moved} ${result.cards_moved === 1 ? 'item' : 'itens'}`,
                            });
                          } catch (err) {
                            showToast({ message: `Erro ao mover grupo: ${err.message}` });
                          }
                        }}
                      />
                    )}
                  </div>
                )}
                <div className="flex-1" />
              </div>
            </th>
          </tr>
          {!effectiveCollapsed && (
            <tr className="sticky top-0 z-20 border-b border-[color:var(--group-border)]" style={{ backgroundColor: 'var(--group-bg)' }}>
              <th className="relative text-left p-0 border-r border-b border-[color:var(--group-border)] overflow-hidden sticky left-0 z-30" style={{ width: titleColWidth, minWidth: titleColWidth, backgroundColor: 'var(--group-bg)' }}>
                <div className="absolute inset-y-0 left-0 w-1.5 rounded-tl-lg" style={{ backgroundColor: group.color }} />
                <div className="flex items-center h-full pl-1.5">
                  {canEditContent && (
                    <>
                      <div className="flex items-center justify-center px-3 self-stretch shrink-0">
                        <input
                          type="checkbox"
                          checked={cards.length > 0 && cards.every((c) => selectedIds?.has(c.id))}
                          onChange={(e) => onToggleSelectGroup?.(cards.map((c) => c.id), e.target.checked)}
                          className="w-[18px] h-[18px] rounded border-border bg-transparent accent-accent cursor-pointer"
                        />
                      </div>
                    </>
                  )}
                  <div className="flex-1 min-w-0 flex items-center justify-center border-l border-[color:var(--group-border)] self-stretch">
                    <span className="col-header-text font-semibold truncate">Elemento</span>
                  </div>
                </div>
                {editMode && (
                  <ResizeHandle
                    onResize={(w) => setTitleColWidth(w)}
                    onResizeEnd={(w) => { setTitleColWidth(w); updateBoard(currentBoard.id, { title_col_width: w }); }}
                  />
                )}
              </th>
              <SortableContext items={columns.map((c) => `col:${c.id}`)} strategy={horizontalListSortingStrategy}>
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className="relative p-0 border-r border-b border-[color:var(--group-border)]"
                    style={{ width: col.width || 150, minWidth: col.width || 150, backgroundColor: 'var(--group-bg)' }}
                  >
                    <ColumnHeader column={col} />
                    {editMode && (
                      <ResizeHandle
                        onResize={(w) => setLiveWidth(col.id, w)}
                        onResizeEnd={(w) => {
                          setLiveWidth(col.id, w);
                          updateColumn(col.id, { width: w }).then(() => clearLiveWidth(col.id));
                        }}
                      />
                    )}
                  </th>
                ))}
              </SortableContext>
              <th className="w-10 p-0 border-b border-[color:var(--group-border)]" style={{ backgroundColor: 'var(--group-bg)' }}>
                {canEditStructure && onAddColumn && (
                  <button
                    onClick={onAddColumn}
                    title="Adicionar coluna"
                    className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-white/10 rounded transition-colors mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </th>
            </tr>
          )}
        </thead>
        {!effectiveCollapsed && (
          <tbody style={{ backgroundColor: 'var(--group-bg)' }}>
            <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
              {cards.map((card) => (
                <CardRow
                  key={card.id}
                  card={card}
                  columns={columns}
                  groupColor={group.color}
                  selected={selectedIds?.has(card.id)}
                  onToggleSelect={onToggleSelect}
                  canCreate={canEditContent}
                />
              ))}
            </SortableContext>
            <NewItemRow groupId={group.id} columnsCount={columns.length}>
              <div className="flex items-center sticky left-0 z-10 w-max">
                <div
                  className="w-1.5 self-stretch shrink-0 min-h-[44px]"
                  style={{ backgroundColor: group.color }}
                />
                <div>
                {showNewCard ? (
                  <div className="flex items-center gap-2 px-2 py-1">
                    <input
                      ref={newCardRef}
                      value={newCardTitle}
                      onChange={(e) => setNewCardTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateCard();
                        if (e.key === 'Escape') {
                          setShowNewCard(false);
                          setNewCardTitle('');
                        }
                      }}
                      onBlur={() => {
                        if (!newCardTitle.trim()) {
                          setShowNewCard(false);
                          setNewCardTitle('');
                        }
                      }}
                      placeholder="Nome do item..."
                      className="w-80 bg-transparent border-none outline-none text-sm text-text-primary placeholder-text-secondary/50 px-2 py-1.5"
                    />
                  </div>
                ) : canEditContent ? (
                  <div className="flex items-center">
                    <button
                      onClick={() => setShowNewCard(true)}
                      className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-text-secondary hover:text-accent transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Novo Item
                    </button>
                    <span className="text-text-secondary/40">|</span>
                    <button
                      onClick={handleCreateDivider}
                      className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-text-secondary hover:text-text-primary transition-colors"
                      title="Adicionar divisória entre itens"
                    >
                      <Minus className="w-3.5 h-3.5" />
                      Nova Divisória
                    </button>
                  </div>
                ) : <div className="min-h-[40px]" />}
                </div>
              </div>
            </NewItemRow>
            {cards.length > 0 && <GroupFooter group={group} columns={columns} cards={cards} />}
          </tbody>
        )}
      </table>
    </div>
  );
}

// React.memo com comparacao shallow default: re-renderiza so quando algum prop
// muda de referencia. Depende de BoardTable passar props estaveis (cards
// memoizado por grupo, callbacks via useCallback, columns/selectedIds memoizados).
export default memo(GroupSection);
